<template>
    <div id="dag-container">
      <svg
        :viewBox="`0 0 ${graphWidth} ${graphHeight}`"
        ref="svgRoot">
      </svg>
    </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';

const d3 = require('d3');
const d3dag = require('d3-dag');
const _ = require('lodash');

export default {
  name: 'ModelDAGVisualization',
  props: {
    defaultGraphWidth: { default: 900, type: Number },
    defaultGraphHeight: { default: 450, type: Number },
    staticColoring: { default: false, type: Boolean },
    boxWidth: { default: 130, type: Number },
    boxHeight: { default: 30, type: Number },
    boxHorizontalSpacing: { default: 10, type: Number },
    boxVerticalSpacing: { default: 10, type: Number },
    margin: { default: () => ({top: 3, bottom: 3, left: 3, right: 3}), type: Object }
  },
  data() {
    return { selections: {} };
  },
  mounted() {
    this.selections.root = d3.select(this.$refs.svgRoot);
    this.createLegend();
    this.createGraph();
  },
  watch: {
    dagVersion: function() {
      // TODO: Dynamically adjust instead of recreating whole graph
      this.selections.graph.remove();
      this.createGraph();
    },
    label: function() {
      this.updateForecastColoring();
    }
  },
  computed: {
    ...mapState(['model','label']),
    ...mapGetters(['dagVersion','layout']),
    graphWidth() {
      return this.layout?.dagWidth ?? this.defaultGraphWidth;
    },
    graphHeight() {
      return this.layout?.dagHeight ?? this.defaultGraphHeight;
    }
  },
  methods: {
    ...mapMutations(['selectLabels']),
    isSelected(selection, current) {
      if (Array.isArray(selection)) {
        return _.difference(selection, current).length === 0;
      } else {
        return selection === current;
      }
    },
    forecastClassColor(d, i, nodes) {
      let labels = this.model.getDisplayComponents(d.id);
      let component = this.model.getComponent(labels);
      let method = (component || {}).method;

      let classes = '';

      if (this.isSelected(this.label, labels)) {
        classes += 'highlighted ';
      }

      if (this.staticColoring) {
        classes += 'static-color-rect';
      } else if (Array.isArray(component)) {
        classes += 'dag-viz-node node'
      } else if (method) {
        classes += 'dag-viz-node aggregation';
      } else {
        classes += 'dag-viz-node data';
      }

      return classes;
    },
    updateForecastColoring() {
      this.selections.rectangles.attr('class', this.forecastClassColor);
    },
    createLegend() {
      // Add legend
      let items = [
        {'class': 'node', 'text': 'Multi-Component Node'},
        {'class': 'aggregation', 'text': 'Aggregation Node'},
        {'class': 'data', 'text': 'Data Node'}
      ];

      this.selections.legend = this.selections.root
        .append('g')
          .attr('transform', `translate(${this.graphWidth-220}, 20)`)
          .attr('display', this.staticColoring ? 'none' : '')
        .selectAll('g')
          .data(items)
          .enter()
        .append('g');
   
      this.selections.legend
        .append('rect')
          .attr('width', 10)
          .attr('height', 10)
          .attr('transform', (_,i) => `translate(0, ${i*20})`)
          .attr('stroke', 'black')
          .attr('class', d => `legend dag-viz-node ${d.class}`)
          .on('click', d => {// On click, move highlight to clicked item
            var models = [];
            d3.selectAll(`.dag-viz-node.${d.class}:not(.legend)`)
              .each(d => models.push(d.id));

            this.selectLabels(models);
          });

      this.selections.legend
        .append('text')
          .attr('transform', (_,i) => `translate(15, ${i*20+10})`)
          .text(d => d.text);
    },
    createGraph() {
      this.selections.graph = this.selections.root.append('g');

      // Set the parent DIV to the requested graph size
      d3.select('#dag-container')
        .style('max-width', `${this.graphWidth}px`)
        .style('max-height', `${this.graphHeight}px`);

      // Create the DAG hierarchy and layout
      let linkData = this.model.getDisplayDAG();
      let dag = d3dag.dagConnect()(linkData);

      let layout = d3dag.sugiyama()
        .nodeSize([this.boxWidth + this.boxHorizontalSpacing, this.boxHeight + this.boxVerticalSpacing]);

      dag = layout(dag);

      // Plot lines between each node
      let line = d3.line()
        .curve(d3.curveBundle)
        .x(d => d.x)
        .y(d => d.y);

      this.selections.lines = this.selections.graph
        .append('g')
        .selectAll('path')
          .data(dag.links())
          .enter()
        .append('path')
          .attr('d', d => line(d.points))
          .attr('class', d => `dag-lines skip-${d.target.layer !== d.source.layer}`);

      // Create each of the nodes
      this.selections.nodes = this.selections.graph
        .append('g')
        .selectAll('g')
          .data(dag.descendants())
          .enter()
        .append('g')
          .attr('transform', ({x, y}) => `translate(${x}, ${y})`);

      // Add a rectangle for each node
      this.selections.rectangles = this.selections.nodes
        .append('rect')
          .attr('transform', `translate(${-this.boxWidth/2}, ${-this.boxHeight/2})`)
          .attr('width', this.boxWidth)
          .attr('height', this.boxHeight)
          .attr('stroke', 'black')
          .attr('class', 'dag-viz-node')
          .on('click', d => {// On click, move highlight to clicked item
            let labels = this.model.getDisplayComponents(d.id);
            this.selectLabels(labels);
          })
          .classed('highlighted', (_,i) => i === 0); // Highlight the root initially

      // Center the graph within the SVG canvas (with origin at 0,0)
      let descendants = dag.descendants();
      let rightShift = (this.graphWidth - d3.max(descendants, n => n.x)) / 2;
      let downShift = (this.graphHeight - d3.max(descendants, n => n.y)) / 2;

      this.selections.graph
        .attr('transform', `translate(${rightShift}, ${downShift})`);

      // Add text to each node
      this.selections.text = this.selections.nodes
        .append('text')
          .text(d => d.id)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('pointer-events', 'none')
          .attr('class', _ => this.staticColoring ? 'static-color-text' : 'normal-text')
          .attr('font-size', d =>
            `${Math.min(1, this.boxWidth / d.id.length / 10)}em`
          );     

      this.updateForecastColoring();
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>
#dag-container {
  margin: auto;
}

.highlighted {
  stroke-width: 3;
  stroke: #ff541e;
}

.dag-lines {
  fill: none;
  stroke-width: 2;
  stroke: black;
}
.dag-lines.skip-true {
  stroke: gray;
}


.normal-text {
  font-weight: bold;
  fill: black;
}

.static-color-rect {
  fill: #0666B4;
}
.static-color-text {
  font-weight: bold;
  fill: #ffffff;
}

.dag-viz-node.data {
  fill: #c0ccd7;
}
.dag-viz-node.data:hover {
  fill: #e0e6eb;
}
.dag-viz-node.data:active {
  fill: #ffffff;
}

.dag-viz-node.node {
  fill: #81e484;
}
.dag-viz-node.node:hover {
  fill: #abedad;
}
.dag-viz-node.node:active {
  fill: #d5f6d6;
}

.dag-viz-node.aggregation {
  fill: #93C6E5;
}
.dag-viz-node.aggregation:hover {
  fill: rgb(175, 215, 240);
}
.dag-viz-node.aggregation:active {
  fill: rgb(193, 227, 248);
}
</style>
